import {
  InvocationType,
  InvokeCommand,
  LambdaClient,
} from "@aws-sdk/client-lambda";
import type {
  TRouteWithRedirect,
  TSuccessResponse,
} from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import axios from "axios";

import type { BFFResponseType } from "../types/bff";

export const BFF_ENDPOINT =
  import.meta.env.MODE === "development"
    ? "http://localhost:5173"
    : import.meta.env.VITE_BFF_ENDPOINT;

export const BFF_PATH = "/_plat/api/v2";

export const BFF_BASE_URL = `${BFF_ENDPOINT}${BFF_PATH}`;

const FunctionName = `user_function_${import.meta.env.VITE_AWS_ENV || "prd"}`;

if (typeof global !== "undefined" && !global.LambdaClientInstance) {
  console.log("creating new LambdaClientInstance as not exists");
  global.LambdaClientInstance = new LambdaClient({
    region: "ap-southeast-1",
    maxAttempts: 0,
    logger: console,
  });
} else {
  console.log("Reuse LambdaClientInstance already exists");
}

function isRunningOnAWS() {
  return !!(typeof process !== "undefined" && process.env.AWS_EXECUTION_ENV);
}

// this file includes all helper functions to server requests to the BFF
export async function BFFInvoker<T = BFFResponseType>(requestPath: string) {
  return isRunningOnAWS()
    ? await AWSInvoker<T>(requestPath)
    : await HTTPInvoker<T>(requestPath);
}

async function HTTPInvoker<T = unknown>(requestPath: string): Promise<T> {
  const res = await axios.get<T>(`${BFF_BASE_URL}${requestPath}`);
  return res.data;
}

async function AWSInvoker<T = unknown>(requestPath: string): Promise<T> {
  const errorResponse = { code: 500, response: {} };

  try {
    const event = {
      body: null,
      httpMethod: "GET",
      path: `${BFF_PATH}${requestPath}`,
      requestContext: {},
    };

    // Execute the user function request
    const params = {
      FunctionName,
      InvocationType: InvocationType.RequestResponse,
      Payload: Buffer.from(JSON.stringify(event, null, 2)),
      Qualifier: "latest",
    };

    console.log("AWSInvoker: invoking requestPath", requestPath);
    const command = new InvokeCommand(params);
    const userFunctionResponse = global.LambdaClientInstance
      ? await global.LambdaClientInstance.send(command)
      : null;

    console.log(
      "Callback from invoking user function Lambda:",
      requestPath,
      // userFunctionResponse,
    );

    if (!userFunctionResponse) {
      console.log("Error invoking user function Lambda:", requestPath);
      return errorResponse as T;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const payloadObject = userFunctionResponse.Payload
      ? JSON.parse(Buffer.from(userFunctionResponse.Payload).toString())
      : {};

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const finalBFFResponse = JSON.parse(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payloadObject.body as string,
    );

    console.log("AWSInvoker: response from BFF Lambda:", requestPath);

    return finalBFFResponse as T;
  } catch (error) {
    console.log("AWSInvoker: ERROR invoking BFF Lambda:", requestPath, error);
    return errorResponse as T;
  }
}

type IFetchDataResponseHandler = BFFResponseType & {
  hasDirtyParams?: boolean;
  redirectUrl?: string;
  generateRedirectFile?: boolean;
  cacheControlMaxAge?: TSuccessResponse<unknown>["cacheControlMaxAge"];
  regenerateInterval?: TSuccessResponse<unknown>["regenerateInterval"];
  dependentPaths?: TSuccessResponse<unknown>["dependentPaths"];
};

/**
 * @description return response handler for fetchData
 * @description keep whole BFFResponse as payload, involves useRouteResponse hook
 * @description redirect handler for 301, 302, which will be defined in BFF Response
 */
export type ReturnFetchDataResponseHandler = TRouteWithRedirect<
  BFFResponseType & Record<keyof BFFResponseType, unknown>,
  BFFResponseType & Record<keyof BFFResponseType, unknown>
>;

/**
 * @description map response from BFF to web2-core's response handler
 */
export const withResponseHandler = ({
  code,
  response,
  message,
  hasDirtyParams = false,
  redirectUrl,
  generateRedirectFile,
  cacheControlMaxAge,
  regenerateInterval,
  dependentPaths,
}: IFetchDataResponseHandler): ReturnFetchDataResponseHandler => {
  const CLIENT_ERROR_STATUS_CODE = 403;

  if (hasDirtyParams) {
    console.log(`Data Fetcher Response: Request is dirty, returning 404:`);

    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: CLIENT_ERROR_STATUS_CODE,
      payload: { code, response, message },
      dependentPaths,
      // message: "Data Fetcher Response: Invalid request",
    };
  }

  // redirect handler
  if ([301, 302].includes(code) && redirectUrl) {
    return {
      type: ResponseType.REDIRECT,
      statusCode: code as 301 | 302,
      target: redirectUrl,
      ...(generateRedirectFile === true ? { generateRedirectFile: true } : {}),
    };
  }

  // client error handler
  if ([400, 401, 402, 403, 404].includes(code)) {
    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: CLIENT_ERROR_STATUS_CODE,
      payload: { code, response, message },
      dependentPaths,
    };
  }

  // server error handler
  if ([500, 502, 503, 504].includes(code)) {
    return {
      type: ResponseType.SERVER_ERROR,
      statusCode: code as 500 | 502 | 503 | 504,
      payload: { code, response, message },
    };
  }

  // success handler
  return {
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: { code, response, message },
    cacheControlMaxAge,
    regenerateInterval,
    dependentPaths,
  };
};
